import { baseApi } from './baseApi';
import { Customer } from 'models';
import { CustomerCreate } from 'models/Customer';
import { BillResponse } from 'models/SaleOrder';
import { QueryParams, TAG_TYPES } from 'types';
import { getQueryParams } from 'utils';

const URL_CUSTOMER = 'customers';

const customerApiService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCustomers: builder.query<
      {
        data: {
          rows: Customer[];
          count: number;
        };
        success: boolean;
      },
      { query: QueryParams }
    >({
      query: (params) => {
        const queryParams = params?.query || params;
        const query = getQueryParams(queryParams);
        if (query === '') return { url: URL_CUSTOMER, method: 'GET' };
        return {
          url: `${URL_CUSTOMER}?${query}`,
          method: 'GET',
        };
      },
      providesTags: (result) => {
        return result
          ? [
              ...(result?.data.rows || []).map(({ id }) => ({ type: TAG_TYPES.Customer, id })),
              { type: TAG_TYPES.Customer, id: 'LIST' },
            ]
          : [{ type: TAG_TYPES.Customer, id: 'LIST' }];
      },
    }),

    getCustomerById: builder.query<{ data: Customer }, { id: string; query?: QueryParams }>({
      query: ({ id, query }) => {
        const queryString = query ? `?${getQueryParams(query)}` : '';

        return {
          url: `${URL_CUSTOMER}/${id}${queryString}`,
          method: 'GET',
        };
      },
      providesTags: (result, error, { id }) => [{ type: TAG_TYPES.Customer, id }],
    }),

    createCustomer: builder.mutation<{ success: boolean; data: Customer }, Omit<CustomerCreate, 'id'>>({
      query: (data) => ({
        url: URL_CUSTOMER,
        method: 'POST',
        data,
      }),
      invalidatesTags: [{ type: TAG_TYPES.Customer, id: 'LIST' }],
    }),

    updateCustomer: builder.mutation<{ success: boolean; data: Customer }, { id: string } & CustomerCreate>({
      query: ({ id, ...patch }) => ({
        url: `${URL_CUSTOMER}/${id}`,
        method: 'PUT',
        data: patch,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: TAG_TYPES.Customer, id },
        { type: TAG_TYPES.Customer, id: 'LIST' },
      ],
    }),

    deleteCustomer: builder.mutation<void, string>({
      query: (id) => ({
        url: `${URL_CUSTOMER}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: TAG_TYPES.Customer, id },
        { type: TAG_TYPES.Customer, id: 'LIST' },
      ],
    }),

    exportAllDebts: builder.mutation<BillResponse, { id: string }>({
      query: ({ id }) => ({
        url: `${URL_CUSTOMER}/${id}/all-debts-pdf`,
        method: 'POST',
      }),
      invalidatesTags: [
        { type: TAG_TYPES.Customer, id: 'LIST' },
        { type: TAG_TYPES.Debt, id: 'LIST' },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetCustomersQuery,
  useLazyGetCustomersQuery,
  useGetCustomerByIdQuery,
  useCreateCustomerMutation,
  useUpdateCustomerMutation,
  useDeleteCustomerMutation,
  useExportAllDebtsMutation,
} = customerApiService;
